import React, { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCheck, Filter, Search, Clock, Zap, AlertTriangle, CheckCircle, DollarSign, FileText, Brain, RefreshCw, X } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const ICON_MAP = {
  enrich_complete: <CheckCircle size={16} color="var(--success)" />,
  export_complete: <FileText size={16} color="var(--info)" />,
  plan_upgraded: <Zap size={16} color="var(--warning)" />,
  search_complete: <Search size={16} color="var(--accent-primary)" />,
  search_failed: <AlertTriangle size={16} color="var(--error)" />,
  payment_review_required: <DollarSign size={16} color="var(--error)" />,
  payment_underpaid: <DollarSign size={16} color="var(--error)" />,
  payment_overpaid: <DollarSign size={16} color="var(--warning)" />,
  lead_updated: <Brain size={16} color="var(--accent-secondary)" />,
};

function formatTimeAgo(timeStr) {
  if (!timeStr) return '';
  try {
    const then = new Date(timeStr);
    const now = new Date();
    const diff = Math.floor((now - then) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return timeStr; }
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // all, unread
  const [hasMore, setHasMore] = useState(false);

  const limit = 30;

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filter === 'unread') params.unread_only = true;
      const res = await api.get('/api/leads/notifications', { params });
      setNotifications(prev => page === 1 ? res.data.notifications : [...prev, ...res.data.notifications]);
      setTotal(res.data.total);
      setUnreadCount(res.data.unread_count);
      setHasMore(res.data.has_more);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const handleMarkRead = async (id) => {
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Persist to backend
    try {
      const dbId = String(id).startsWith('admin_') ? parseInt(String(id).replace('admin_', '')) : id;
      await api.post('/api/admin/notifications/mark-read', { notification_id: dbId });
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/api/admin/notifications/mark-read', { mark_all: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch {
      toast.error('Failed to mark all read');
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      setPage(prev => prev + 1);
    }
  };

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 24px)', maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bell size={24} color="var(--accent-primary)" /> Notifications
          </h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}
            style={{
              padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-primary)',
              background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 500,
            }}>
            <option value="all">All</option>
            <option value="unread">Unread</option>
          </select>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8, border: '1px solid var(--success)40',
              background: 'transparent', color: 'var(--success)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>
              <CheckCheck size={14} /> Mark All Read
            </button>
          )}
          <button onClick={() => { setPage(1); loadNotifications(); }} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--info)40',
            background: 'transparent', color: 'var(--info)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
      }}>
        {loading && notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Bell size={40} style={{ marginBottom: 16, opacity: 0.2 }} />
            <p style={{ color: 'var(--text-tertiary)', fontSize: 14, margin: 0 }}>No notifications yet</p>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 4 }}>Notifications from searches, enrichments, and exports will appear here.</p>
          </div>
        ) : (
          <>
            {notifications.map((n, i) => (
              <div key={n.id} style={{
                padding: '14px 18px', cursor: 'pointer',
                borderBottom: i < notifications.length - 1 ? '1px solid var(--border-primary)' : 'none',
                background: n.read ? 'transparent' : 'var(--accent-glow)',
                display: 'flex', gap: 12, alignItems: 'flex-start',
                transition: 'background 0.15s ease',
              }}
                onClick={() => !n.read && handleMarkRead(n.id)}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                onMouseLeave={e => e.currentTarget.style.background = n.read ? 'transparent' : 'var(--accent-glow)'}>
                <span style={{ flexShrink: 0, marginTop: 2 }}>
                  {ICON_MAP[n.type] || <span style={{ fontSize: 16 }}>{n.icon || 'Bell'}</span>}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>{n.title}</span>
                    {!n.read && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-primary)', flexShrink: 0, marginTop: 4 }} />
                    )}
                  </div>
                  {n.description && (
                    <p style={{ color: 'var(--text-secondary)', fontSize: 12, margin: '4px 0 0', lineHeight: 1.5 }}>{n.description}</p>
                  )}
                  <div style={{ color: 'var(--text-tertiary)', fontSize: 10, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={10} /> {formatTimeAgo(n.time)}
                  </div>
                </div>
                {n.link && (
                  <a href={n.link} style={{ color: 'var(--accent-primary)', fontSize: 11, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                    onClick={e => e.stopPropagation()}>
                    View →
                  </a>
                )}
              </div>
            ))}
            {hasMore && (
              <div style={{ padding: '14px 18px', textAlign: 'center', borderTop: '1px solid var(--border-primary)' }}>
                <button onClick={loadMore} disabled={loading} style={{
                  padding: '8px 20px', borderRadius: 8, border: '1px solid var(--accent-primary)40',
                  background: 'transparent', color: 'var(--accent-primary)', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, opacity: loading ? 0.6 : 1,
                }}>
                  {loading ? 'Loading...' : `Load more (${total - notifications.length} remaining)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
